import { forwardRef, useEffect } from 'react';
import type { MouseEvent, PropsWithoutRef } from 'react';
import styled from 'styled-components';

import type { NoChildrenProp, RefElement, WithAttributes } from '../../types';
import { defaultThemeProp } from '../../theme';
import Button from '../Button';
import Icon, { registerIcon } from '../Icon';
import * as dockIcon from '../Icon/icons/dock.icon';
import * as undockIcon from '../Icon/icons/undock.icon';
import { useConsolidatedRef, useI18n } from '../../hooks';

import { useFullscreenContext } from './Fullscreen';

registerIcon(dockIcon, undockIcon);

export type FullscreenButtonProps = WithAttributes<'button', NoChildrenProp>;

export const StyledFullscreenButton = styled(Button)``;

StyledFullscreenButton.defaultProps = defaultThemeProp;

export default forwardRef<
  RefElement<FullscreenButtonProps>,
  PropsWithoutRef<FullscreenButtonProps>
>(function FullscreenButton({ onClick, ...restProps }, ref) {
  const t = useI18n();
  const ctxVal = useFullscreenContext();
  const btnRef = useConsolidatedRef(ref);

  useEffect(() => {
    if (!ctxVal || !btnRef.current) return;

    if (ctxVal[0] !== undefined) {
      btnRef.current.focus();
    }
  }, [ctxVal && ctxVal[0]]);

  if (!ctxVal) return null;

  const [fullscreen, toggleFullscreen] = ctxVal;

  return (
    <StyledFullscreenButton
      {...restProps}
      ref={btnRef}
      icon
      variant='simple'
      label={t(fullscreen ? 'exit_fullscreen' : 'enter_fullscreen')}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        toggleFullscreen();
        onClick?.(e);
      }}
    >
      <Icon name={fullscreen ? 'dock' : 'undock'} />
    </StyledFullscreenButton>
  );
});
